<template>
  <div>
    <base-header class="pb-6" type>
      <div class="row align-items-center py-4">
        <breadcrumbs :header="$t('Video library')"></breadcrumbs>
      </div>
    </base-header>
    <div class="container-fluid mt--6">
      <div class="row">
        <div class="col">
          <div id="uppyBlock">
            <uppy-widget
              v-if="optionsLoaded"
              :uploadUrl="tusUrl"
              :chunkSize="tusChunkSize"
              @complete="onComplete"
            ></uppy-widget>
            <div class="text-muted my-2">
              <p class="small" v-html="$t('videoUpload.message')"></p>
              <p class="small" v-html="$t('videoUpload.messageLimit')"></p>
            </div>
          </div>
        </div>
      </div>
      <v-wait :for="waitFor">
        <template slot="waiting">
          <div class="container loader">
            <div class="row justify-content-md-center">
              <div class="col-md-auto">
                <spinner></spinner>
              </div>
            </div>
          </div>
        </template>
        <div class="row">
          <div class="col">
            <div class="card bg-transparent">
              <el-table
                class="table-responsive"
                header-row-class-name="thead-light"
                empty-text="У вас пока нету загруженных видео"
                :data="videos"
              >
                <el-table-column
                  class-name="align-middle pl-4"
                  width="426px"
                  :label="$t('Preview')"
                  prop="thumbnail"
                >
                  <template v-slot="{row}">
                    <div class="card mb-0" style="max-width: 426px">
                      <div class="card-img-top rounded-top">
                        <playerjs-player
                          v-if="row.isReady"
                          :ref="`playerJs${row.id}`"
                          :id="row.id"
                          :output-url="row.outputUrl"
                          :poster="row.thumbnailUrl"
                        >
                        </playerjs-player>
                        <img
                          v-if="row.thumbnailUrl"
                          v-show="!row.isReady"
                          :alt="row.fileName"
                          :id="`videoPoster${row.id}`"
                          :src="row.thumbnailUrl"
                        />
                      </div>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('File info')" min-width="210px" prop="name">
                  <template v-slot="{row}">
                    <strong>{{ row.fileName }}</strong>
                    <br />
                    <span
                      class="mr-1"
                      v-if="row.dimensions"
                    >{{ $t('video.dimensions', {dimensions: row.dimensions}) }}</span>
                    <br />
                    <span v-if="row.duration">{{ $t('video.duration', {duration: row.duration}) }}</span><br />
                    <a :href="row.sourcePath">
                      SRC
                    </a>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('Status')" min-width="120px" prop="status">
                  <template v-slot="{row}">
                    <badge :type="getBadgeType(row.status)">{{ $t(`videoStatus.${row.status}`) }}</badge>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('Video size')" prop="details" min-width="140px">
                  <template v-slot="{row}">{{ $t('video.size', {size: row.size}) }}</template>
                </el-table-column>
                <el-table-column :label="$t('Actions')">
                  <template v-slot="{row}">
                     <el-dropdown trigger="click" class="dropdown">
                        <span class="btn btn-flat">
                          <i class="fas fa-ellipsis-v"></i>
                        </span>
                        <el-dropdown-menu class="dropdown-menu dropdown-menu-arrow show" slot="dropdown">
                          <a class="dropdown-item text-warning" @click="confirmDelete(row.id)">
                            <i class="fas fa-trash-alt"></i>
                            {{ $t('Delete') }}
                          </a>
                        </el-dropdown-menu>
                      </el-dropdown>
                  </template>
                </el-table-column>
              </el-table>
              <modal-delete-confirmation
                v-if="videos && videos.length > 0"
                ref="video-delete-confirmation"
                v-model="showModal"
                :text="textModal"
                :id="idModal"
                @confirmed="onDeleteVideo">
              </modal-delete-confirmation>
            </div>
          </div>
        </div>
      </v-wait>
    </div>
  </div>
</template>

<script>
import Breadcrumbs from "../../Layout/Breadcrumbs";

import Spinner from "vue-spinner-component/src/Spinner";
import {Dropdown, DropdownMenu, Table, TableColumn, Tooltip} from "element-ui";
import UppyWidget from "../../../components/Uppy/UppyWidget";

import { createNamespacedHelpers } from "vuex";
const { mapState, mapActions } = createNamespacedHelpers("course/video");

import {_} from 'vue-underscore';
import PlayerjsPlayer from "../../../components/Players/PlayerjsPlayer";
import {PlayerjsMixin} from "../../../mixins/Video/Playerjs";
import {DeleteConfirmation} from "../../../mixins/DeleteConfirmation";
import ModalDeleteConfirmation from "../../../components/Modals/ModalDeleteConfirmation";

export default {
  name: "CourseVideo",
  metaInfo() {
    return {
      title: this.$i18n.t("Videos"),
    };
  },
  components: {
    PlayerjsPlayer,
    UppyWidget,
    Breadcrumbs,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    Spinner,
    ModalDeleteConfirmation
  },
  mixins: [
    PlayerjsMixin,
    DeleteConfirmation
  ],
  data() {
    return {
      waitFor: "video list fetching",
    };
  },
  computed: {
    ...mapState({
      videos: (state) => state.videos,
      options: (state) => state.uploadOptions,
    }),
    textModal() {
      return this.$i18n.t('CourseVideo.deleteVideo.textModal')
    },
    optionsLoaded() {
      return this.tusUrl && this.tusChunkSize;
    },
    tusUrl() {
      return this.options.tusUploadUrl;
    },
    tusChunkSize() {
      return Number(this.options.tusChunkSize);
    },
    getBadgeType() {
      return (status) => {
        switch (status) {
          case "ready":
            return "success";
          case "uploaded":
            return "secondary";
          case "worker_error":
            return "error";
          default:
            return "primary";
        }
      };
    },
  },
  async created() {
    this.loadPlayerJs();
    await this.fetchData();
  },
  watch: {
    $route: "fetchData",
  },
  methods: {
    ...mapActions({
      load: "loadVideos",
      deleteVideo: "deleteVideo",
      getOptions: "getUploadOptions",
    }),
    async fetchData() {
      this.$wait.start(this.waitFor);
      await this.getOptions({ urlParams: this.$route.params });
      await this.load({ urlParams: this.$route.params });
      this.$wait.end(this.waitFor);
    },
    onComplete($el) {
      $el.successful.length > 0 && this.fetchData();
    },
    async onDeleteVideo(id) {
      this.$wait.start('video deleting');
      await this.deleteVideo({
        urlParams: {...this.$route.params, videoId: id}
      });

      this.$wait.end('video deleting');
    },

  },
};
</script>

<style lang="scss">
@import "~video.js/dist/video-js.css";
</style>
